import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Building2, Users, List } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { WorksSummary } from "@/components/dashboard/WorksSummary";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { RecentAlerts } from "@/components/dashboard/RecentAlerts";
import { UpcomingTasks } from "@/components/dashboard/UpcomingTasks";
import { AISuggestions } from "@/components/dashboard/AISuggestions";
import { WorkPanel } from "@/components/work-panel/WorkPanel";
import { NovaObraModal } from "@/components/obras/NovaObraModal";
import { DashboardItemsPopup, DashboardItemsConfig } from "@/components/dashboard/DashboardItemsPopup";
import { NovoUsuarioModal } from "@/components/usuario/NovoUsuarioModal";
import { PreencherRelatorioModal } from "@/components/rdo/PreencherRelatorioModal";
import { getCurrentUserName, getCurrentUserEmail, getCurrentUserAvatarUrl } from "@/utils/userUtils";

const Dashboard = () => {
  const [searchParams] = useSearchParams();
  const workId = searchParams.get('obra');
  const [isNovaObraModalOpen, setIsNovaObraModalOpen] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isItemsPopupOpen, setIsItemsPopupOpen] = useState(false);
  const [isNovoUsuarioModalOpen, setIsNovoUsuarioModalOpen] = useState(false);
  
  const [dashboardConfig, setDashboardConfig] = useState<DashboardItemsConfig>({
    dashboardHeader: true,
    worksSummary: true,
    quickActions: true,
    quickActionsRelatorio: true,
    quickActionsRDO: true,
    quickActionsTarefas: true,
    quickActionsSugestoes: true,
    quickActionsCadastros: true,
    quickActionsNovaObra: true,
    recentAlerts: true,
    upcomingTasks: true,
    aiSuggestions: true,
  });

  // Se há um ID de obra na URL, mostra o painel específico da obra
  const isWorkPanel = !!workId;

  return (
    <div className="flex-1 overflow-auto">
      {isWorkPanel ? (
        <WorkPanel />
      ) : (
        <div className="p-4 sm:p-6 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <BackButton />
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => setIsItemsPopupOpen(true)}
                className="gap-2"
              >
                <List className="h-4 w-4" />
                Itens
              </Button>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setIsReportDialogOpen(true)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Preencher RDO
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setIsNovaObraModalOpen(true)}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Adicionar Obra
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setIsNovoUsuarioModalOpen(true)}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Adicionar Usuário
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
          
          {/* Dashboard Header — identidade real do usuário logado (profile) */}
          {dashboardConfig.dashboardHeader && (
            <DashboardHeader
              userName={getCurrentUserName()}
              userRole={getCurrentUserEmail() ?? ""}
              userAvatar={getCurrentUserAvatarUrl() ?? undefined}
            />
          )}

          {/* Works Summary */}
          {dashboardConfig.worksSummary && <WorksSummary />}

          {/* Quick Actions */}
          {dashboardConfig.quickActions && (
            <QuickActions config={dashboardConfig} />
          )}

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left Column - Alerts and Tasks */}
            <div className="xl:col-span-2 space-y-6">
              {dashboardConfig.recentAlerts && (
                <RecentAlerts 
                  isVisible={dashboardConfig.recentAlerts}
                  onVisibilityChange={(visible) => 
                    setDashboardConfig(prev => ({ ...prev, recentAlerts: visible }))
                  }
                />
              )}
              {dashboardConfig.upcomingTasks && (
                <UpcomingTasks 
                  isVisible={dashboardConfig.upcomingTasks}
                  onVisibilityChange={(visible) => 
                    setDashboardConfig(prev => ({ ...prev, upcomingTasks: visible }))
                  }
                />
              )}
            </div>
            
            {/* Right Column - AI Suggestions */}
            <div className="xl:col-span-1">
              {dashboardConfig.aiSuggestions && (
                <AISuggestions 
                  isVisible={dashboardConfig.aiSuggestions}
                  onVisibilityChange={(visible) => 
                    setDashboardConfig(prev => ({ ...prev, aiSuggestions: visible }))
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Nova Obra Modal (salva no Supabase internamente) */}
      <NovaObraModal
        open={isNovaObraModalOpen}
        onOpenChange={setIsNovaObraModalOpen}
      />

      {/* Preencher Relatório Modal */}
      <PreencherRelatorioModal
        open={isReportDialogOpen}
        onOpenChange={setIsReportDialogOpen}
        defaultObra={workId || undefined}
      />

      {/* Dashboard Items Popup */}
      <DashboardItemsPopup
        open={isItemsPopupOpen}
        onOpenChange={setIsItemsPopupOpen}
        config={dashboardConfig}
        onConfigChange={setDashboardConfig}
      />

      {/* Novo Usuário Modal */}
      <NovoUsuarioModal 
        open={isNovoUsuarioModalOpen}
        onOpenChange={setIsNovoUsuarioModalOpen}
      />
    </div>
  );
};

export default Dashboard;